import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import {
  fetchHeVendorApprovalEmailPdf,
  fetchHeVendorApprovalEmailPreview,
  fetchUnsavedHeVendorCopy,
  fetchUnsavedHeVendorEmailPreview,
  type HeVendorApprovalEmailPreview,
  type HeVendorEmailDraft,
  type HiredEquipmentSubmitPayload,
} from '../../../services/hiredEquipmentService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export function isValidVendorEmailDraftAddress(email: string): boolean {
  const trimmed = email.trim();
  return trimmed.length > 3 && trimmed.length < 320 && EMAIL_RE.test(trimmed);
}

export interface HeVendorApprovalEmailModalProps {
  isOpen: boolean;
  formId: string | null;
  /**
   * H1: Operations Coordinator submit. The record does not exist yet – the preview is built from
   * this payload, the buttons read "Submit…", and Cancel saves nothing.
   */
  submitPayload?: HiredEquipmentSubmitPayload | null;
  /** True while approval (and any vendor send) is in flight. Actions stay locked. */
  busy?: boolean;
  onCancel: () => void;
  onSkip: () => void | Promise<void>;
  onSend: (draft: HeVendorEmailDraft) => void | Promise<void>;
}

const emptyDraft: HeVendorEmailDraft = { to: '', subject: '', bodyText: '' };

const HeVendorApprovalEmailModal: React.FC<HeVendorApprovalEmailModalProps> = ({
  isOpen,
  formId,
  submitPayload = null,
  busy = false,
  onCancel,
  onSkip,
  onSend,
}) => {
  const [preview, setPreview] = useState<HeVendorApprovalEmailPreview | null>(null);
  const [draft, setDraft] = useState<HeVendorEmailDraft>(emptyDraft);
  // V2-015 / CONF-03: only the button that was pressed shows progress; the other is just disabled.
  const [pressed, setPressed] = useState<'skip' | 'send' | null>(null);
  useEffect(() => {
    if (!busy) setPressed(null);
  }, [busy]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<{ blob: Blob; filename: string } | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const submitMode = submitPayload != null;

  useEffect(() => {
    if (!isOpen || (!formId && !submitPayload)) {
      setPreview(null);
      setDraft(emptyDraft);
      setLoadError(null);
      setActionError(null);
      setPdfFile(null);
      setPdfError(null);
      setPdfLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setActionError(null);
    setPdfFile(null);
    setPdfError(null);
    void (submitPayload ? fetchUnsavedHeVendorEmailPreview(submitPayload) : fetchHeVendorApprovalEmailPreview(formId!))
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setDraft({
          to: data.vendorEmail || '',
          subject: data.subject,
          bodyText: data.bodyText,
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Failed to load email preview');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, formId, submitPayload]);

  const toValid = isValidVendorEmailDraftAddress(draft.to);
  const senderOk = Boolean(preview?.replyToEmail && isValidVendorEmailDraftAddress(preview.replyToEmail));
  const canSendDraft = Boolean(
    preview?.sendgridConfigured
    && senderOk
    && toValid
    && draft.subject.trim()
    && draft.bodyText.trim(),
  );
  const cannotSendReason = useMemo(() => {
    if (!preview?.cannotSendReason) return null;
    if (/no contact email/i.test(preview.cannotSendReason) && toValid) return null;
    return preview.cannotSendReason;
  }, [preview, toValid]);

  const handleSkip = async () => {
    if (busy) return;
    setActionError(null);
    setPressed('skip');
    try {
      await onSkip();
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : submitMode ? 'Could not submit' : 'Failed to approve');
    }
  };

  const handleSend = async () => {
    if (busy || !canSendDraft) return;
    setActionError(null);
    setPressed('send');
    try {
      await onSend({
        to: draft.to.trim(),
        subject: draft.subject.trim(),
        bodyText: draft.bodyText.trim(),
      });
    } catch (error: unknown) {
      setActionError(
        error instanceof Error ? error.message : submitMode ? 'Could not submit and email the vendor' : 'Failed to approve and email the vendor',
      );
    }
  };

  const updateDraft = (patch: Partial<HeVendorEmailDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const triggerPdfDownload = (file: { blob: Blob; filename: string }) => {
    const url = URL.createObjectURL(file.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    if ((!formId && !submitPayload) || pdfLoading) return;
    if (pdfFile) {
      triggerPdfDownload(pdfFile);
      return;
    }
    setPdfLoading(true);
    setPdfError(null);
    try {
      const file = submitPayload ? await fetchUnsavedHeVendorCopy(submitPayload) : await fetchHeVendorApprovalEmailPdf(formId!);
      setPdfFile(file);
      triggerPdfDownload(file);
    } catch (error: unknown) {
      setPdfError(error instanceof Error ? error.message : 'Failed to download copy');
    } finally {
      setPdfLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={busy ? () => undefined : onCancel}
      title={submitMode ? 'Submit hired equipment' : 'Email the vendor a copy?'}
      size="medium"
      footer={
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={() => void handleSkip()} disabled={busy}>
            {busy && pressed === 'skip' ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-2" />
                Processing…
              </>
            ) : (
              submitMode ? 'Submit without sending' : 'Approve without sending'
            )}
          </Button>
          <Button
            variant="success"
            onClick={() => void handleSend()}
            disabled={busy || loading || !canSendDraft}
          >
            {busy && pressed === 'send' ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-2" />
                Processing…
              </>
            ) : (
              submitMode ? 'Submit and email PDF' : 'Approve and email PDF'
            )}
          </Button>
        </div>
      }
    >
      {submitMode ? (
        <p className="tw-mb-3 tw-text-sm tw-text-content">
          This submission will be saved in the <strong>Approved</strong> state. You can email the vendor a
          PDF copy now – edit the message if needed and download a copy to review it – or submit without
          sending. Nothing is saved until you choose one.
        </p>
      ) : (
        <p className="tw-mb-3 tw-text-sm tw-text-content">
          Approval is not finished until you complete this step. Edit the message if needed, download a
          copy to review it, then send it – or approve without sending.
        </p>
      )}

      {loading && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-content-muted">
          <Spinner animation="border" size="sm" />
          Loading email preview…
        </div>
      )}

      {loadError && (
        <Alert variant="warning" className="tw-text-sm">
          {loadError} You can still {submitMode ? 'submit' : 'approve'} without sending.
        </Alert>
      )}

      {actionError && (
        <Alert variant="danger" className="tw-text-sm">
          {actionError}
        </Alert>
      )}

      {preview && (
        <>
          {cannotSendReason && (
            <Alert variant="warning" className="tw-text-sm">
              {cannotSendReason}
            </Alert>
          )}
          {!toValid && draft.to.trim() !== '' && (
            <Alert variant="warning" className="tw-text-sm">
              Enter a valid To address before sending.
            </Alert>
          )}
          <div className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm">
            <p className="tw-mb-3 tw-font-display tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">
              Email preview
            </p>
            <Form>
              <Form.Group controlId="he-vendor-email-to">
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="email"
                  value={draft.to}
                  onChange={(event) => updateDraft({ to: event.target.value })}
                  disabled={busy}
                  isInvalid={Boolean(draft.to.trim()) && !toValid}
                  autoComplete="off"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>From</Form.Label>
                <p className="tw-mb-0 tw-text-sm tw-text-content">
                  {preview.fromName} &lt;{preview.fromEmail}&gt;
                </p>
              </Form.Group>
              <Form.Group>
                <Form.Label>Reply-To</Form.Label>
                <p className="tw-mb-0 tw-text-sm tw-text-content">{preview.replyToEmail || '–'}</p>
              </Form.Group>
              <Form.Group controlId="he-vendor-email-subject">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  type="text"
                  value={draft.subject}
                  onChange={(event) => updateDraft({ subject: event.target.value })}
                  disabled={busy}
                  maxLength={300}
                />
              </Form.Group>
              <Form.Group controlId="he-vendor-email-body">
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={draft.bodyText}
                  onChange={(event) => updateDraft({ bodyText: event.target.value })}
                  disabled={busy}
                  maxLength={20000}
                  className="tw-font-mono tw-text-sm"
                />
              </Form.Group>
            </Form>
          </div>

          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm">
            <div>
              <p className="tw-mb-1 tw-font-display tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">
                Attached PDF
              </p>
              <p className="tw-mb-0 tw-font-mono tw-text-xs tw-text-content">
                {preview.attachmentFilename}
              </p>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => void handleDownloadPdf()}
              disabled={busy || pdfLoading}
            >
              {pdfLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Preparing file…
                </>
              ) : (
                'Download copy'
              )}
            </Button>
          </div>
          {pdfError && (
            <Alert variant="warning" className="tw-mt-3 tw-text-sm">
              {pdfError} You can still send if the email looks right; the PDF is generated again at
              send time.
            </Alert>
          )}
        </>
      )}
    </ResponsiveModal>
  );
};

export default HeVendorApprovalEmailModal;
